import { useCallback } from 'react';
import type { Element, TargetingElementKeysType } from '../context/types';

export const useElementOperations = () => {
  const enableElement = useCallback((element: Element): Element => {
    return {
      ...element,
      mainOption: { ...element.mainOption, isDisabled: false },
      subOptions: element.subOptions?.map(option => ({
        ...option,
        isDisabled: false,
      })),
    };
  }, []);

  const disableElement = useCallback((element: Element): Element => {
    return {
      ...element,
      mainOption: { ...element.mainOption, isDisabled: true, isSelected: false, isError: false },
      subOptions: element.subOptions?.map(option => ({
        ...option,
        isDisabled: true,
        isSelected: false,
        isError: false,
      })),
    };
  }, []);

  const selectElement = useCallback((element: Element): Element => {
    return {
      ...element,
      mainOption: { ...element.mainOption, isDisabled: false, isSelected: true },
      subOptions: element.subOptions?.map(option => ({
        ...option,
        isDisabled: false,
        isSelected: true,
      })),
    };
  }, []);

  const deselectElement = useCallback((element: Element): Element => {
    return {
      ...element,
      mainOption: { ...element.mainOption, isSelected: false },
      subOptions: element.subOptions?.map(option => ({
        ...option,
        isSelected: false,
      })),
    };
  }, []);

  const markErrorInElement = useCallback((element: Element): Element => {
    return {
      ...element,
      mainOption: { ...element.mainOption, isError: true },
    };
  }, []);

  const selectElementByKey = useCallback(
    (elements: Element[], key: TargetingElementKeysType | null): Element[] => {
      return elements.map(el => (el.mainOption.key === key ? selectElement(el) : el));
    },
    [selectElement]
  );

  const deselectElementByKey = useCallback(
    (elements: Element[], key: TargetingElementKeysType | null): Element[] => {
      return elements.map(el => (el.mainOption.key === key ? deselectElement(el) : el));
    },
    [deselectElement]
  );

  const disableElementByKey = useCallback(
    (elements: Element[], key: TargetingElementKeysType | null): Element[] => {
      return elements.map(el => (el.mainOption.key === key ? disableElement(el) : el));
    },
    [disableElement]
  );

  const enableElementByKey = useCallback(
    (elements: Element[], key: TargetingElementKeysType | null): Element[] => {
      return elements.map(el => (el.mainOption.key === key ? enableElement(el) : el));
    },
    [enableElement]
  );

  return {
    enableElement,
    disableElement,
    selectElement,
    deselectElement,
    selectElementByKey,
    deselectElementByKey,
    enableElementByKey,
    disableElementByKey,
    markErrorInElement,
  };
};
